import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MenuItem } from '../core/models/administration/menu-item';
import { HttpClient } from '@angular/common/http';
import { CONFIG } from '../core/config';
import { DropdownItem } from '../core/models/dropdown-item';
import { ConvocationDropdownItem } from '../core/models/administration/global-codes/convocation/convocation-dropdown-item';
import { catchError, finalize } from 'rxjs/operators';
import { ExceptionService } from '../core/services/exception.service';
import { LoginType } from '../core/models/administration/system-parameters/login-type';
import { SpinnerService } from '../core/spinner/spinner.service';
import { OrgUnitUsersDropdownItem } from '../core/models/administration/users/org-unit-users-dropdown-item';

@Injectable({
    providedIn: 'root'
})
export class SharedService {

    constructor(private _http: HttpClient,
        private _exceptionService: ExceptionService,
        private _spinnerService: SpinnerService) { }

    getNavMenu(langID: number): Observable<MenuItem[]> {
        return this._http.get<MenuItem[]>(CONFIG.baseUrls.nav.getNavMenu + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => { /*console.log("test")*/ })
            );
    }

    getContentType(documentExtension: string) {
        switch (documentExtension) {
            case CONFIG.fileExtensions.TEXT:
                return "text/plain";
            case CONFIG.fileExtensions.PDF:
                return "application/pdf";
            case CONFIG.fileExtensions.DOC:
                return "application/msword";
            case CONFIG.fileExtensions.DOCX:
                return "application/msword";
            case CONFIG.fileExtensions.EXCEL:
                return "application/vnd.ms-excel";
            case CONFIG.fileExtensions.EXCELX:
                return "application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet";
            case CONFIG.fileExtensions.PNG:
                return "image/png";
            case CONFIG.fileExtensions.JPG:
                return "image/jpeg";
            case CONFIG.fileExtensions.JPEG:
                return "image/jpeg";
            case CONFIG.fileExtensions.GIF:
                return "image/gif";
            case CONFIG.fileExtensions.CSV:
                return "text/csv";
        }
    }

    getReportContentType(documentType: string) {
        switch (documentType) {
            case CONFIG.reportContentType.PDF:
                return "application/pdf";
            case CONFIG.reportContentType.EXCEL:
                return "application/msexcel";
            case CONFIG.reportContentType.WORD:
                return "application/msword";
        }
    }
    getReportFileExtension(documentType: string) {
        switch (documentType) {
            case CONFIG.reportContentType.PDF:
                return ".pdf";
            case CONFIG.reportContentType.EXCEL:
                return ".xls";
            case CONFIG.reportContentType.WORD:
                return ".doc";
        }
    }   

    getAllDocumentTypesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllDocumentTypesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllConfirmationRegistrationTypesForDropdown( langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllConfirmationRegistrationTypesForDropdown +  langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllGendersForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllGendersForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllSensitiveCategoriesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllSensitiveCategoriesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllOrganizationalUnitsForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllOrganizationalUnitsForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllOrganizationalUnitUsersForDropdown(id: number, onlyActive: boolean, langID: number): Observable<OrgUnitUsersDropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<OrgUnitUsersDropdownItem[]>(CONFIG.baseUrls.shared.getAllOrganizationalUnitUsersForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllProfilesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllProfilesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getInternalProfilesForDropdown(id: number, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getInternalProfilesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
        );
    }

    getAllOrganizationalUnitFunctionsForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllOrganizationalUnitFunctionsForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllLoginTypesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<LoginType[]> {
        this._spinnerService.show();
        return this._http.get<LoginType[]>(CONFIG.baseUrls.shared.getAllLoginTypesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllCategoriesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllCategoriesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllComplaintStatusesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllComplaintStatusesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllConfigurationNotificationsForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllConfigurationNotificationsForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllDebateStatusesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllDebateStatusesForDropdown + (id == null ? 0 : id) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllPrivilegesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllPrivilegesForDropdown + (id == null ? 0 : id) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllDebateCategoryForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllDebateCategoryForDropdown + (id == null ? 0 : id) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllDebateKindForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllDebateKindForDropdown + (id == null ? 0 : id) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllSessionStatusesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllSessionStatusesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllDebateSessionStatusesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllDebateSessionStatusesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllDebateSessionTypesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllDebateSessionTypesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllAgendaStatusesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllAgendaStatusesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllPoliticalPartiesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllPoliticalPartiesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllConvocationsForDropdown(id: number, onlyActive: boolean, langID: number): Observable<ConvocationDropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<ConvocationDropdownItem[]>(CONFIG.baseUrls.shared.getAllConvocationsForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllCouncilMemberTypesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllCouncilMemberTypesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllDebateTypesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllDebateTypesForDropdown + (id == null ? 0 : id)  +"/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllQuestionTypesForDropdown(id: number, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllQuestionTypesForDropdown + (id == null ? 0 : id) + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllSurveyTypesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllSurveyTypesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllSurveyCategoriesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllSurveyCategoriesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllSurveyParticipantTypesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllSurveyParticipantTypesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllSurveyStatusesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllSurveyStatusesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllAdmProcedureRefersToForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllAdmProcedureRefersToForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllAdmProcedureTypesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllAdmProcedureTypesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllAdmProcedureStatusesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllAdmProcedureStatusesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllAdmProcedureSophisticationLevelsForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllAdmProcedureSophisticationLevelsForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllAdmProcedureOutputTypesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllAdmProcedureOutputTypesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllAdmProcedureLegalRemediesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllAdmProcedureLegalRemediesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllDocumentDeliveryFormsForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllDocumentDeliveryFormsForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getAllAdministrativeProcedureCategoriesForDropdown(id: number, onlyActive: boolean, langID: number): Observable<DropdownItem[]> {
        this._spinnerService.show();
        return this._http.get<DropdownItem[]>(CONFIG.baseUrls.shared.getAllAdmProcedureCategoriesForDropdown + (id.toString() == null ? 0 : id.toString()) + "/" + onlyActive + "/" + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
}
